<!--
  Popup — модалка/дровер в body. Одна анимация на открытие: подложка гаснет
  фейдом, контент въезжает сверху (translateY).

  Раньше здесь было три перекрывающихся перехода: <transition> вокруг
  <teleport v-if> (leave ломался — контент исчезал мгновенно, обёртка
  дожидалась конца), вложенный TransitionTranslateY mode="out-in" (его стили
  живут в Navigation.vue под :deep и до телепортированного узла не долетают)
  и собственный 0.5s transition у .color-overlay. Открытие выглядело как
  двойное срабатывание и рывки.

  Теперь: teleport снаружи (он не элемент и анимироваться не может), внутри —
  ОДИН Transition со своими стилями, объявленными здесь же, без зависимости от
  классов layout-компонентов.

  Teleport рендерится только после монтирования на клиенте (isClient). Это
  прямая рекомендация документации Vue для SSR: контент телепорта не попадает
  в основную разметку (уходит в ctx.teleports), а таргет body «impossible for
  Teleports to determine the correct starting location for hydration» — на
  сервере такой телепорт рендерить нельзя, иначе ломается гидратация страницы.
  См. vuejs.org/guide/scaling-up/ssr#teleports.

  СТЕК ПОПАПОВ ДЕРЖИТСЯ НА ПОРЯДКЕ УЗЛОВ В BODY — не трогать условие телепорта.
  У всех попапов один и тот же z-index-6, поэтому сверху рисуется тот, кто позже
  в body. Vue ставит якорь телепорта в body в момент МОНТИРОВАНИЯ телепорта и
  потом всегда вставляет контент на место этого якоря. Значит телепорт обязан
  монтироваться на ОТКРЫТИИ (isRendered) — тогда попап, открытый из другого
  попапа, ложится поверх него.

  Уже наступали: с `v-if="isClient"` телепорт монтируется вместе с компонентом,
  порядок в body становится порядком монтирования страниц — и алерт из
  alert.plugin.js (монтируется на старте приложения) уезжает ПОД попап страницы
  (смонтирован при заходе на страницу). Симптом: `proxy.$alert` из открытого
  попапа не виден. Плюс это гонка ленивых чанков, так что «под/над» плавает.

  Чтобы при этом не потерялась leave-анимация, телепорт живёт до конца перехода:
  isRendered гасится в @after-leave, а не по isPopupOpen. Transition монтируется
  вместе с содержимым, поэтому ему нужен appear — иначе enter не сыграет.
-->
<template>
  <!-- isRendered, а не isPopupOpen: см. блок про стек попапов выше. -->
  <teleport to="body" v-if="isClient && isRendered">
    <transition name="popup" appear @after-leave="onAfterLeave">
      <div v-if="isPopupOpen" class="z-index-6 popup-wrapper"
        :class="{
          'pd-t-extra': MOBILE_APP === 'ios',
          [alignClass]: true
        }"
      >
        <div class="popup-overlay z-index-3" @click="closePopup()"></div>

        <section class="popup-content h-max-100 o-y-scroll pos-relative z-index-4" v-bind="$attrs">
          <h4 v-if="title" class="mn-b-medium">{{title}}</h4>
          <div class="pos-absolute h-100 pos-t-0 pos-r-0 no-events">
            <button @click="closePopup()" style="pointer-events: auto" class="button button--accent pd-micro radius-extra pos-sticky pos-t-0 pos-r-0 mn-t-medium mn-r-medium hover-scale-1 z-index-5">
              <IconCross class="fill-white dark:fill-white i-regular"/>
            </button>
          </div>
          <slot></slot>
        </section>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, watch, onMounted, ref, nextTick } from 'vue';
// Import libs
import { useRoute, useRouter } from 'vue-router'
// import { useI18n } from 'vue-i18n'
// Icons
import IconCross from '@martyrs/src/modules/icons/navigation/IconCross.vue';
// Define props
const props = defineProps({
  isPopupOpen: {
    type: Boolean,
    default: false
  },
  style: String,
  title: String,
  align: {
    type: String,
    default: 'center center',
    validator: (value) => {
      const validAlignments = [
        'top left', 'top center', 'top right',
        'center left', 'center center', 'center right',
        'bottom left', 'bottom center', 'bottom right'
      ];
      return validAlignments.includes(value);
    }
  }
});

const emits = defineEmits(['close-popup', 'after-leave'])

// Compute the alignment class from the align prop
const alignClass = computed(() => {
  return `align-${props.align.replace(/\s+/g, '-')}`;
});

// Teleport — только на клиенте (см. комментарий у шаблона и доку Vue по SSR).
const isClient = ref(false);
onMounted(() => { isClient.value = true; });

// Телепорт смонтирован: включается на ОТКРЫТИИ (от этого зависит порядок узлов
// в body и, значит, стек попапов — см. комментарий у шаблона), гасится только
// после leave-анимации, иначе контент исчезнет мгновенно.
const isRendered = ref(props.isPopupOpen);

function closePopup() {
  emits("close-popup");
}

function onAfterLeave() {
  isRendered.value = false;
  emits("after-leave");
}

watch(() => props.isPopupOpen, (newVal) => {
  if (newVal) isRendered.value = true;
  // На сервере document нет — класс вешаем только в браузере.
  if (typeof document === 'undefined') return;
  if (newVal) {
    document.body.classList.add('no-scroll');
  } else {
    document.body.classList.remove('no-scroll');
  }
});

// Accessing router and store
const route = useRoute()
const router = useRouter()
// Localization
const text = {
  messages: {
    en: {},
    ru: {}
  }
}
// const { t } = useI18n(text)
</script>

<style lang="scss">
  .no-scroll {
    overflow: hidden;

    #view {
      overflow: hidden;
    }
  }

  .popup-wrapper {
    display: flex;
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 40;

    /* Default alignment - center center */
    &.align-center-center {
      align-items: center;
      justify-content: center;
    }

    /* Top alignments */
    &.align-top-left {
      align-items: flex-start;
      justify-content: flex-start;
    }

    &.align-top-center {
      align-items: flex-start;
      justify-content: center;
    }

    &.align-top-right {
      align-items: flex-start;
      justify-content: flex-end;
    }

    /* Center alignments */
    &.align-center-left {
      align-items: center;
      justify-content: flex-start;
    }

    &.align-center-right {
      align-items: center;
      justify-content: flex-end;
    }

    /* Bottom alignments */
    &.align-bottom-left {
      align-items: flex-end;
      justify-content: flex-start;
    }

    &.align-bottom-center {
      align-items: flex-end;
      justify-content: center;
    }

    &.align-bottom-right {
      align-items: flex-end;
      justify-content: flex-end;
    }

    .popup {
      position: absolute;
      height: auto;
      padding: 2rem;
      min-width: 24rem;
      border-radius: 2rem;
      overflow: hidden;
      color: white;
      background: black;
      z-index: 40;
    }
  }

  /* Подложка попапа. Своего transition НЕ имеет — гаснет вместе с обёрткой
     в общей анимации .popup-*, иначе получаются два перехода разной
     длительности на одно открытие. */
  .popup-overlay {
    display: block;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.25);
  }

  /* Появление попапа: сама панель только проявляется (без сдвига — иначе
     фуллскрин-панель «въезжает» и снизу видна щель). Движение содержимого
     задаёт потребитель, см. .popup-content > * ниже. */
  .popup-enter-active,
  .popup-leave-active {
    transition: opacity 0.2s ease;
  }
  .popup-enter-from,
  .popup-leave-to {
    opacity: 0;
  }

  /* Вторая фаза: когда панель уже проявилась, её содержимое въезжает сверху. */
  .popup-enter-active .popup-content > * {
    animation: popup-content-in 0.34s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.14s;
  }
  @keyframes popup-content-in {
    from { opacity: 0; transform: translateY(-1rem); }
    to   { opacity: 1; transform: none; }
  }

  /* .color-overlay остаётся для потребителей вне Popup (ShopCart и т.п.). */
  .color-overlay {
    display: block;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.0);
    pointer-events: none;
    transform: scale(1.5);
    // Blur задан в обоих состояниях (0 → 12px), иначе backdrop-filter не анимируется
    -webkit-backdrop-filter: blur(0px) saturate(100%);
    backdrop-filter: blur(0px) saturate(100%);
    transition: all 0.5s cubic-bezier(.31,.79,.24,.92);

    &.active {
      background: rgba(#000,0.25);
      // Frosted-glass: размытие + лёгкая пересатурация фона под затенением.
      // Оверлей монтируется сразу с .active (v-if), transition стартовать не с чего —
      // поэтому вход анимируем keyframes-ами, а не transition.
      -webkit-backdrop-filter: blur(12px) saturate(140%);
      backdrop-filter: blur(12px) saturate(140%);
      animation: popup-overlay-in 0.5s cubic-bezier(.31,.79,.24,.92) both;
      pointer-events: all;
    }
  }

  @keyframes popup-overlay-in {
    from {
      background: rgba(0,0,0,0);
      -webkit-backdrop-filter: blur(0px) saturate(100%);
      backdrop-filter: blur(0px) saturate(100%);
    }
    to {
      background: rgba(0,0,0,0.25);
      -webkit-backdrop-filter: blur(12px) saturate(140%);
      backdrop-filter: blur(12px) saturate(140%);
    }
  }
</style>
